import {
  suggestedPromptVariants,
  type SmartCartAgentSuggestedPrompt,
} from '@hungryroot/rest';

const SESSION_STORAGE_KEY = 'smart-cart-agent-pending-prompt';
const FRESH_CHAT_KEY = 'smart-cart-agent-pending-fresh-chat';

/**
 * Hands a suggested prompt off to the Smart Cart Agent panel. Surfaces that
 * open the panel indirectly (e.g. the Exp 224 Variant 2 conversation
 * starters in search) store the prompt here; the panel consumes it once its
 * initial load settles and runs it through the normal prompt-click path.
 */
/* Storage alone cannot wake an already-mounted panel: it reads the key from
   effects whose dependencies a second hand-off does not touch, so the prompt
   would sit unread until something unrelated re-rendered. The version counter
   gives subscribers a value that changes on every hand-off. */
let version = 0;
const listeners = new Set<() => void>();

export function setPendingPrompt(prompt: SmartCartAgentSuggestedPrompt): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(prompt));
  } catch {
    // sessionStorage unavailable
  }
  // Bumped even when the write throws, so a panel that is listening still
  // reacts rather than silently ignoring the click.
  version += 1;
  listeners.forEach((listener) => listener());
}

/** Subscribe to hand-offs. Returns an unsubscribe for `useSyncExternalStore`. */
export function subscribeToPendingPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getPendingPromptVersion(): number {
  return version;
}

/**
 * The "Chat with Rooty" chip: open a fresh conversation with nothing to send.
 * Same hand-off path as a prompt, minus the message.
 */
export function requestFreshChat(): void {
  try {
    sessionStorage.setItem(FRESH_CHAT_KEY, '1');
  } catch {
    // sessionStorage unavailable
  }
  version += 1;
  listeners.forEach((listener) => listener());
}

/** Reads and clears the fresh-chat request. */
export function consumeFreshChatRequest(): boolean {
  try {
    const requested = sessionStorage.getItem(FRESH_CHAT_KEY) !== null;
    sessionStorage.removeItem(FRESH_CHAT_KEY);
    return requested;
  } catch {
    return false;
  }
}

/** Peek without consuming, so a caller can reset before it commits to acting. */
export function hasPendingHandoff(): boolean {
  try {
    return (
      sessionStorage.getItem(SESSION_STORAGE_KEY) !== null ||
      sessionStorage.getItem(FRESH_CHAT_KEY) !== null
    );
  } catch {
    return false;
  }
}

/** Drops a hand-off that can no longer run, so it cannot fire later. */
export function clearPendingHandoff(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(FRESH_CHAT_KEY);
  } catch {
    // sessionStorage unavailable
  }
}

/** Reads and clears the pending prompt so it only ever runs once. */
export function consumePendingPrompt(): SmartCartAgentSuggestedPrompt | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    sessionStorage.removeItem(SESSION_STORAGE_KEY);

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'label' in parsed &&
      typeof parsed.label === 'string' &&
      'type' in parsed &&
      (parsed.type === suggestedPromptVariants.prefill ||
        parsed.type === suggestedPromptVariants.autosend)
    ) {
      return { label: parsed.label, type: parsed.type };
    }
    return null;
  } catch {
    return null;
  }
}
